import { useToken } from '@chakra-ui/react'
import { useMemo } from 'react'
import {
  Bar,
  BarChart,
  CartesianGrid,
  Customized,
  LabelList,
  Legend,
  Tooltip,
} from 'recharts'

import { CustomLegend } from '../StackedChart/customChartComponents/CustomLegend'
import { FloatingChartTooltipShell } from '../chartTooltip/ChartTooltipPointerContext'
import type { FloatingCartesianExtraRow } from '../chartTooltip/FloatingCartesianChartTooltip'
import { FloatingCartesianChartTooltip } from '../chartTooltip/FloatingCartesianChartTooltip'
import { CategoryAxisTick } from '../common/CategoryAxisTick'
import { GroupedVerticalBarTotalsLayer } from '../common/GroupedVerticalBarTotalsLayer'
import { RechartsWrapper } from '../common/RechartsWrapper'
import {
  BAR_CORNER_RADIUS,
  CHART_GRID_STROKE,
  CHART_HEIGHT,
  CHART_MIN_WIDTH,
  MIN_WIDTH_PER_ITEM,
  SNAPSHOT_ANGLED_AXIS_BAND_HEIGHT,
  SNAPSHOT_ANGLED_X_AXIS,
  SNAPSHOT_CHART_HEIGHT,
  SNAPSHOT_CHART_MARGIN,
  SNAPSHOT_VERTICAL_BAR_AXIS_PADDING_PX,
  SNAPSHOT_VERTICAL_BAR_CATEGORY_GAP,
  SNAPSHOT_VERTICAL_BAR_SIZE,
  VERTICAL_BAR_ANGLED_TICK_DX,
  VERTICAL_BAR_ANGLED_TICK_DY,
  VERTICAL_BAR_ANGLED_X_AXIS_HEIGHT,
  VERTICAL_BAR_CLUSTER_WIDTH_PX,
  VERTICAL_BAR_INTER_CATEGORY_GAP_PX,
  VERTICAL_BAR_Y_AXIS_TICK_DX,
} from '../common/chart.constants'
import { ChartData, ChartValue } from '../common/chart.types'
import { formatChartDataLabelValue } from '../common/chartFormat'
import { ChartGrid } from '../common/chartGridLines'
import { ChartTopNUtils } from '../common/chartTopN'
import { useChartColors } from '../common/useChartColors'
import { useCustomRecharts } from '../common/useCustomRecharts'
import {
  StackedVerticalTotalLabel,
  type VerticalBarTotalLabelProps,
} from '../common/verticalBarTotalLabels'

export type SeriesItem = {
  name: string
  dataKey: string
  color: string
}

export interface VerticalBarChartProps {
  chartData: ChartData
  colorScheme?: 'positive' | 'negative' | 'neutral'
  series?: SeriesItem[]
  /** Stack bars on top of each other instead of side-by-side. */
  stacked?: boolean
  /** Override chart height (px). */
  chartHeight?: number
  /** Hide the built-in legend (render it externally instead). */
  hideLegend?: boolean
  /** Enable horizontal scrolling when there are many data points. */
  horizontalScroll?: boolean
  /** Extra row keys on each data point (e.g. cube `count`) for the tooltip. */
  floatingTooltipExtraRows?: FloatingCartesianExtraRow[]
  /** When true, show totals above each category (grouped/stacked) or above single bars. */
  showBarValueLabels?: boolean
  /** Force Y-axis ceiling. Port-to-Port grouped bar Figma: 70. */
  yAxisMax?: number
  yAxisTickCount?: number
  /** Homepage snapshot charts: wider bars, percent category gap, no bar stroke. */
  variant?: 'default' | 'snapshot'
}

export const VerticalBarChart = ({
  chartData,
  colorScheme = 'neutral',
  series,
  stacked = false,
  chartHeight,
  hideLegend = false,
  horizontalScroll = false,
  floatingTooltipExtraRows,
  showBarValueLabels = false,
  yAxisMax,
  yAxisTickCount,
  variant = 'default',
  ...props
}: VerticalBarChartProps) => {
  const isSnapshot = variant === 'snapshot'
  const chartColors = useChartColors()
  const measureKey = chartData.valueKey ?? 'value'
  const displayChartValues = useMemo(() => {
    const rows = (chartData.chartValues ?? []) as ChartValue[]
    if (chartData.dataTopN == null) return rows
    return ChartTopNUtils.selectTopRowsByMeasure(
      rows,
      measureKey,
      chartData.dataTopN,
    )
  }, [chartData.chartValues, chartData.dataTopN, measureKey])
  const { XAxis, YAxis, chartWrapperRef } = useCustomRecharts({
    chartData: { ...chartData, chartValues: displayChartValues },
    series,
    stacked,
    variant,
    yAxisMax,
    yAxisTickCount,
  })
  const blue = useToken('colors.presets', 'blue.50')
  const red = useToken('colors.presets', 'pink.70')

  const colorSchemes = {
    positive: { positive: blue, negative: red },
    negative: { positive: red, negative: blue },
    neutral: { positive: blue, negative: blue },
  }
  const colors = colorSchemes[colorScheme]
  const barData = series
    ? displayChartValues
    : displayChartValues?.map((entry: any) => ({
        ...entry,
        fill: entry.value >= 0 ? colors.positive : colors.negative,
      }))

  const itemCount = displayChartValues?.length ?? 0
  const Y_AXIS_PADDING = 60
  const scrollableWidth =
    horizontalScroll && itemCount > 0
      ? itemCount * MIN_WIDTH_PER_ITEM + Y_AXIS_PADDING
      : undefined

  const floatingSeriesMeta =
    series?.map(s => ({
      dataKey: s.dataKey,
      name: s.name,
      color: s.color,
    })) ?? []

  const seriesDataKeys = series?.map(s => s.dataKey) ?? []
  const chartRows = displayChartValues as Record<string, unknown>[]

  const seriesCount = series?.length ?? 0
  // Snapshot homepage bars only (`chartVariant: 'snapshot'`). Default = Port-to-Port layout.
  const barWidthPx = isSnapshot
    ? (chartData.snapshotBarWidth ?? SNAPSHOT_VERTICAL_BAR_SIZE)
    : series && seriesCount > 0
      ? stacked
        ? VERTICAL_BAR_CLUSTER_WIDTH_PX
        : VERTICAL_BAR_CLUSTER_WIDTH_PX / seriesCount
      : VERTICAL_BAR_CLUSTER_WIDTH_PX

  const topMarginForBarLabels = showBarValueLabels ? 28 : 4
  const categoryAxisLabelLayout = chartData.categoryAxisLabelLayout ?? 'angled'
  const snapshotAngledAxisBand = chartData.snapshotAngledAxisBand ?? 'medium'
  const xAxisHeight = isSnapshot
    ? categoryAxisLabelLayout === 'flat'
      ? 80
      : SNAPSHOT_ANGLED_AXIS_BAND_HEIGHT[snapshotAngledAxisBand]
    : categoryAxisLabelLayout === 'flat'
      ? 80
      : VERTICAL_BAR_ANGLED_X_AXIS_HEIGHT

  const barChart = (
    <BarChart
      data={barData ?? []}
      margin={isSnapshot ? SNAPSHOT_CHART_MARGIN : undefined}
      {...(isSnapshot && series && !stacked
        ? { barCategoryGap: SNAPSHOT_VERTICAL_BAR_CATEGORY_GAP, barGap: 0 }
        : series && !stacked && { barCategoryGap: 4, barGap: 0 })}
      {...(stacked && { barCategoryGap: 6 })}
    >
      {XAxis({
        dataKey: chartData.nameKey ?? 'key',
        padding: isSnapshot
          ? {
              left: SNAPSHOT_VERTICAL_BAR_AXIS_PADDING_PX,
              right: SNAPSHOT_VERTICAL_BAR_AXIS_PADDING_PX,
            }
          : {
              left: VERTICAL_BAR_INTER_CATEGORY_GAP_PX / 2,
              right: VERTICAL_BAR_INTER_CATEGORY_GAP_PX / 2,
            },
        tick: (
          <CategoryAxisTick
            variant={variant}
            categoryAxisIsDate={chartData.categoryAxisIsDate}
            dateFormat={chartData.categoryAxisDateFormat}
            dateLayout={categoryAxisLabelLayout}
            fill={isSnapshot ? undefined : chartColors.axisTickFill}
            {...(categoryAxisLabelLayout === 'angled' && {
              unparseableTruncate: {
                maxLength: 24,
                angle: -45,
                textAnchor: 'end',
                dx: VERTICAL_BAR_ANGLED_TICK_DX,
                dy: VERTICAL_BAR_ANGLED_TICK_DY,
                fontWeight: 500,
              },
            })}
          />
        ),
        ...(categoryAxisLabelLayout === 'angled' && { dy: 0 }),
        height: xAxisHeight,
        ...(isSnapshot && categoryAxisLabelLayout === 'angled'
          ? SNAPSHOT_ANGLED_X_AXIS
          : {}),
        ...(horizontalScroll && { padding: { left: 10, right: 16 } }),
      })}
      {YAxis({
        padding: { top: topMarginForBarLabels, bottom: 0 },
        ...(horizontalScroll && !isSnapshot && { width: 35 }),
        ...(!isSnapshot && {
          tick: {
            ...chartColors.rechartsAxisTick,
            dx: VERTICAL_BAR_Y_AXIS_TICK_DX,
          },
        }),
      })}
      <CartesianGrid
        stroke={CHART_GRID_STROKE}
        horizontal={ChartGrid.horizontalLine as any}
        syncWithTicks
        vertical={false}
      />
      <Tooltip
        shared={false}
        cursor={false}
        content={
          <FloatingCartesianChartTooltip
            floatingLayout="bar"
            nameKey={chartData.nameKey ?? 'key'}
            series={floatingSeriesMeta}
            extraRows={floatingTooltipExtraRows}
            categoryAxisDateFormat={chartData.categoryAxisDateFormat}
            categoryAxisIsDate={chartData.categoryAxisIsDate}
            valueFormat={chartData.valueFormat}
          />
        }
      />
      {series ? (
        <>
          {!hideLegend && (
            <Legend
              content={<CustomLegend />}
              verticalAlign="top"
              wrapperStyle={{ paddingBottom: 10 }}
            />
          )}
          {series.map((s, index) => (
            <Bar
              key={s.dataKey}
              dataKey={s.dataKey}
              name={s.name}
              fill={s.color}
              {...(!isSnapshot && {
                stroke: chartColors.barStroke,
                strokeWidth: 1,
              })}
              {...(stacked && { stackId: 'stack' })}
              radius={
                stacked && index < series.length - 1
                  ? [0, 0, 0, 0]
                  : [BAR_CORNER_RADIUS, BAR_CORNER_RADIUS, 0, 0]
              }
              {...(isSnapshot
                ? { maxBarSize: barWidthPx }
                : { barSize: barWidthPx })}
              isAnimationActive={false}
            >
              {showBarValueLabels && stacked && index === series.length - 1 ? (
                <LabelList
                  content={props => (
                    <StackedVerticalTotalLabel
                      {...(props as VerticalBarTotalLabelProps)}
                      rows={chartRows}
                      seriesDataKeys={seriesDataKeys}
                      valueFormat={chartData.valueFormat}
                      dataLabelFill={chartColors.dataLabelFill}
                    />
                  )}
                />
              ) : null}
            </Bar>
          ))}
          {showBarValueLabels && !stacked ? (
            <Customized
              component={GroupedVerticalBarTotalsLayer}
              rows={chartRows}
              seriesDataKeys={seriesDataKeys}
              valueFormat={chartData.valueFormat}
              dataLabelFill={chartColors.dataLabelFill}
            />
          ) : null}
        </>
      ) : (
        <Bar
          dataKey="value"
          fill="fill"
          barSize={barWidthPx}
          radius={[BAR_CORNER_RADIUS, BAR_CORNER_RADIUS, 0, 0]}
          background={{
            fill: chartColors.barBackground,
            radius: BAR_CORNER_RADIUS,
          }}
          isAnimationActive={false}
        >
          {showBarValueLabels ? (
            <LabelList
              dataKey="value"
              position="top"
              offset={8}
              fill={chartColors.dataLabelFill}
              fontSize={12}
              fontWeight={700}
              formatter={(v: number) =>
                formatChartDataLabelValue(v, chartData.valueFormat)
              }
            />
          ) : null}
        </Bar>
      )}
    </BarChart>
  )

  const resolvedChartHeight =
    chartHeight ?? (isSnapshot ? SNAPSHOT_CHART_HEIGHT : CHART_HEIGHT)

  const wrapperCommon = {
    ref: chartWrapperRef,
    yAxisLabel: chartData.yAxisLabel,
    xAxisLabel: chartData.xAxisLabel,
    scrollableWidth,
    chartHeight: resolvedChartHeight,
    minW: isSnapshot ? 0 : `${CHART_MIN_WIDTH}px`,
    variant,
    ...props,
  }

  return (
    <FloatingChartTooltipShell>
      {mouseHandlers => (
        <RechartsWrapper
          {...wrapperCommon}
          chartAreaMouseTracking={mouseHandlers}
        >
          {barChart}
        </RechartsWrapper>
      )}
    </FloatingChartTooltipShell>
  )
}
